
import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { FloatingRemyContainer } from './remy/FloatingRemyContainer';
import type { FusedRecommendation } from '@/services/recommendations-fusion.service';
import { ActiveShortlist } from '@/components/recommendations/ActiveShortlist';
import { isWebRemyEnabled } from '@/lib/web-remy-flag';

export interface GlobalRemyContextType {
  isRemyOpen: boolean;
  setIsRemyOpen: (open: boolean) => void;
  isCartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  
  pageContext: string;
  setPageContext: (context: string) => void;
  
  currentCity: string;
  setCurrentCity: (city: string) => void;
  
  // Cart/saved items (shared across pages)
  cartItems: FusedRecommendation[];
  savedSpots: string[];
  addToCart: (item: FusedRecommendation) => void;
  removeFromCart: (id: string) => void;
  saveSpot: (id: string) => void;
  unsaveSpot: (id: string) => void;
  
  hoveredVenue: FusedRecommendation | null;
  setHoveredVenue: (venue: FusedRecommendation | null) => void;
}

const GlobalRemyContext = createContext<GlobalRemyContextType | undefined>(undefined);

export function useGlobalRemy() {
  const context = useContext(GlobalRemyContext);
  if (!context) {
    console.warn('useGlobalRemy: GlobalRemyProvider not found, returning null');
    return null;
  }
  return context;
}

interface GlobalRemyProviderProps {
  children: ReactNode;
}

export function GlobalRemyProvider({ children }: GlobalRemyProviderProps) {
  const [isRemyOpen, setIsRemyOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  
  const [pageContext, setPageContext] = useState('home');
  const [currentCity, setCurrentCity] = useState('');
  
  // Shared cart and saved state
  const [cartItems, setCartItems] = useState<FusedRecommendation[]>([]);
  const [savedSpots, setSavedSpots] = useState<string[]>([]);
  
  // Hover state for map integration
  const [hoveredVenue, setHoveredVenue] = useState<FusedRecommendation | null>(null);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Helper to get stable ID
  const getRecId = useCallback((rec: FusedRecommendation): string => {
    return rec.id || rec.google_place_id || 
      `${rec.place_name}-${rec.detected_neighborhood || rec.neighborhood || currentCity}`
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-');
  }, [currentCity]);
  
  const addToCart = useCallback((item: FusedRecommendation) => {
    const id = getRecId(item);
    setCartItems(prev => {
      if (prev.some(i => getRecId(i) === id)) return prev;
      return [...prev, item];
    });
    setSavedSpots(prev => {
      if (prev.includes(id)) return prev;
      return [...prev, id];
    });
  }, [getRecId]);
  
  const removeFromCart = useCallback((id: string) => {
    setCartItems(prev => prev.filter(item => getRecId(item) !== id));
    setSavedSpots(prev => prev.filter(spotId => spotId !== id));
  }, [getRecId]);
  
  const saveSpot = useCallback((id: string) => {
    setSavedSpots(prev => {
      if (prev.includes(id)) return prev;
      return [...prev, id];
    });
  }, []);
  
  const unsaveSpot = useCallback((id: string) => {
    setSavedSpots(prev => prev.filter(spotId => spotId !== id));
  }, []);

  const openCart = useCallback(() => setIsCartOpen(true), []);
  const closeCart = useCallback(() => setIsCartOpen(false), []);
  
  return (
    <GlobalRemyContext.Provider
      value={{
        isRemyOpen,
        setIsRemyOpen,
        isCartOpen,
        openCart,
        closeCart,
        pageContext,
        setPageContext,
        currentCity,
        setCurrentCity,
        cartItems,
        savedSpots,
        addToCart,
        removeFromCart,
        saveSpot,
        unsaveSpot,
        hoveredVenue,
        setHoveredVenue
      }}
    >
      {children}

      {/* Global FloatingRemy — off unless VITE_ENABLE_WEB_REMY=true. */}
      {isWebRemyEnabled() && !isMobile && (
        <FloatingRemyContainer
          cartItems={cartItems}
          savedPlaces={cartItems}
          city={currentCity}
        />
      )}

      {/* Global Planning Cart drawer — Remy assist copy follows the same kill switch. */}
      <ActiveShortlist
        isOpen={isCartOpen}
        onClose={closeCart}
        items={cartItems.map((i) => ({ ...(i as any), id: getRecId(i) }))}
        onRemoveItem={(id: string) => removeFromCart(id)}
        city={currentCity}
        remyEnabled={isWebRemyEnabled()}
      />
    </GlobalRemyContext.Provider>
  );
}

export function useRemyPageContext(context: string, city?: string) {
  const remy = useGlobalRemy();
  
  React.useEffect(() => {
    if (remy) {
      remy.setPageContext(context);
      if (city) {
        remy.setCurrentCity(city);
      }
    }
  }, [context, city, remy]);
}
